import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

import { Capacitor } from "@capacitor/core";

import { hideNativeSplash } from "@/lib/nativeSplash";
import { setBrowserThemeColor } from "@/lib/browserThemeColor";
import { setNativeStatusBar } from "@/lib/nativeStatusBar";
import "./zuzu-splash.css";

/**
 * The app's startup screen: dew-covered leaf with the stacked zuzu wordmark
 * bouncing — the same letter bounce the in-app loader uses; the brand mark
 * has no other animation. There is exactly one of these — the native
 * Capacitor splash carries the same leaf without the wordmark, so the webview
 * taking over is invisible and the member sees a single continuous screen.
 *
 * Kept separate from {@link ZuzuLoader} on purpose: the loader has no leaf
 * artwork and is for loading screens *inside* the app, never for cold launch.
 */

/**
 * The whole opening beat, measured from launch. The letters bounce from the
 * first frame on a 90ms stagger; capped at 0.5s per product requirement even
 * though a full bounce takes longer — the wordmark simply cuts to the login
 * screen mid-bounce.
 *
 * On the phone this budget covers BOTH halves of the opening screen, not just
 * this one — see {@link splashRemainingMs}.
 */
export const SPLASH_DURATION_MS = 500;

/**
 * How much of the opening beat is left to spend here.
 *
 * The member sees one opening screen; the phone shows two. iOS and Android
 * can't paint web content until the webview has booted, so a native launch
 * screen covers that gap — the same leaf, no wordmark, which is why the
 * handover is invisible. It is unavoidable: nothing of ours runs before it.
 *
 * What we can do is make both halves come out of the same 0.5s instead of
 * taking 0.5s each. `performance.now()` is the time since the webview started
 * loading, which is nearly all of the native half, so deducting it leaves the
 * wordmark exactly the remainder of the budget. A boot slower than the whole
 * budget leaves nothing, and the login screen arrives as soon as it can.
 *
 * On the web there is no native half and no boot to deduct, so the beat runs
 * in full from the moment this screen mounts.
 */
export function splashRemainingMs(): number {
  let native = false;
  try {
    native = Capacitor.isNativePlatform();
  } catch {
    native = false;
  }
  if (!native) return SPLASH_DURATION_MS;
  const spentBooting =
    typeof performance === "undefined" ? 0 : performance.now();
  return Math.max(0, SPLASH_DURATION_MS - spentBooting);
}

export const SPLASH_LEAF_URL = "/splash/leaf-splash-828.jpg";

// Dew droplets scattered over the leaf (decorative).
const DROPS = [
  { l: 14, t: 22, s: 26, d: 0 },
  { l: 72, t: 15, s: 17, d: 1.6 },
  { l: 26, t: 58, s: 20, d: 3.1 },
  { l: 80, t: 64, s: 14, d: 4.4 },
  { l: 47, t: 80, s: 23, d: 2.3 },
  { l: 62, t: 38, s: 12, d: 5.6 },
  { l: 9, t: 72, s: 15, d: 6.8 },
  { l: 88, t: 42, s: 18, d: 0.8 },
];

const Z_PATH =
  "M58.0827 0H11.9168C5.33535 0 0 5.37259 0 12L0 12.75C0 19.3774 5.33536 24.75 11.9169 24.75H23.5874C24.9234 24.75 25.5839 26.3844 24.6275 27.3238L3.60934 47.968C1.31002 50.2265 0.0134183 53.3239 0.0134183 56.5583V57C0.0134183 63.6274 5.34877 69 11.9303 69H58.0602C64.6417 69 69.9771 63.6274 69.9771 57V56.25C69.9771 49.6226 64.6417 44.25 58.0602 44.25H46.2292C44.8938 44.25 44.233 42.617 45.1881 41.6772L66.412 20.792C68.7133 18.5275 70.0073 15.4222 70 12.1821L69.9995 11.9726C69.9845 5.35588 64.6535 0 58.0827 0Z";

const U_PATH =
  "M120.643 34.6589V14.6787C120.643 6.57187 127.214 0 135.321 0C143.428 0 150 6.57187 150 14.6787V55.187C149.066 62.4974 142.85 68.1466 135.321 68.1466C130.394 68.1466 126.03 65.7274 123.34 62.0065C118.322 66.3642 111.782 69 104.63 69C88.8179 69 76 56.1191 76 40.2297V14.8C76 6.62619 82.6262 0 90.8 0C98.9738 0 105.6 6.62619 105.6 14.8V34.6589C105.6 38.8128 108.967 42.1802 113.121 42.1802C117.275 42.1802 120.643 38.8128 120.643 34.6589Z";

export interface ZuzuSplashProps {
  /** Tap anywhere to skip ahead. Omit for the session-resolving gate. */
  onSkip?: () => void;
}

/**
 * True once the leaf JPG has decoded. Until then the splash paints the page
 * canvas colour instead of the leaf's green — on the web the image isn't
 * precached (unlike native, where the Capacitor splash already showed it), so
 * the green fallback used to flash solid for a beat before the leaf arrived.
 */
function useLeafReady(): boolean {
  const [ready, setReady] = useState(() => {
    if (typeof window === "undefined") return true;
    const img = new window.Image();
    img.src = SPLASH_LEAF_URL;
    return img.complete; // already cached → no gate at all
  });
  useEffect(() => {
    if (ready) return undefined;
    let alive = true;
    const img = new window.Image();
    const done = () => {
      if (alive) setReady(true);
    };
    img.src = SPLASH_LEAF_URL;
    if (typeof img.decode === "function") img.decode().then(done, done);
    else {
      img.onload = done;
      img.onerror = done;
    }
    return () => {
      alive = false;
    };
  }, [ready]);
  return ready;
}

export function ZuzuSplash({ onSkip }: ZuzuSplashProps) {
  const leafReady = useLeafReady();
  // Hand the native launch screen off only once our own leaf is up, so the
  // two halves cut together on the same image instead of flashing the empty
  // webview between them.
  useEffect(() => {
    if (leafReady) hideNativeSplash();
  }, [leafReady]);

  // The strip above the page — the clock, and in a browser the tint behind it
  // — belongs to whatever is on screen, and this is the only screen that
  // isn't the cream canvas. Owned for exactly as long as this is up,
  // including as a Suspense fallback or the session gate, which is why it's
  // tied to mount rather than to the opening beat.
  useEffect(() => {
    setNativeStatusBar("splash");
    setBrowserThemeColor("splash");
    return () => {
      setNativeStatusBar("app");
      setBrowserThemeColor("app");
    };
  }, []);

  return (
    <div
      className={`zz-splash${onSkip ? " zz-splash--tappable" : ""}${leafReady ? "" : " zz-splash--loading"}`}
      style={{ backgroundImage: `url('${SPLASH_LEAF_URL}')` }}
      onClick={onSkip}
      role="status"
      aria-label="zuzu"
    >
      <div className="zz-splash-drops" aria-hidden="true">
        {DROPS.map((p, i) => (
          <span
            key={i}
            className="zz-splash-drop"
            style={
              {
                left: `${p.l}%`,
                top: `${p.t}%`,
                "--ds": `${p.s}px`,
                "--dd": `${p.d}s`,
              } as CSSProperties
            }
          >
            <i />
          </span>
        ))}
      </div>
      {/* Each letter is its own group so it can bounce on a stagger — the
          same bounce as the in-app loader, and the only animation the
          wordmark ever gets. */}
      <div className="zz-splash-mark">
        <svg viewBox="0 -26 150 170" fill="currentColor" aria-hidden="true">
          <g className="zzs-l zzs-l1">
            <path d={Z_PATH} />
          </g>
          <g className="zzs-l zzs-l2">
            <path d={U_PATH} />
          </g>
          <g transform="translate(0, 75)">
            <g className="zzs-l zzs-l3">
              <path d={Z_PATH} />
            </g>
          </g>
          <g transform="translate(0, 75)">
            <g className="zzs-l zzs-l4">
              <path d={U_PATH} />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}

export default ZuzuSplash;
